// @ts-check

/** @file Creates one project-domain frontend replica and its private sync state. */

import Migration from "velocious/build/src/database/migration/index.js"

const TABLES_IN_DROP_ORDER = [
  "syncs", "replica_metadata", "comments", "timelogs", "task_assigned_users",
  "task_boards", "tasks", "board_columns", "boards", "task_types"
]

/** Creates the schema repeated independently in every user/project SQLite database. */
export default class CreateProjectReplica extends Migration {
  /** Creates domain, queue, receipt/conflict, and metadata tables atomically. */
  async up() {
    await this.connection().transaction(async () => {
      await this.createDomainTables()
      await this.createBookkeepingTables()
      await this.createIndexes()
    })
  }

  /** Drops only this physical project's replica state. */
  async down() {
    await this.connection().transaction(async () => {
      for (const tableName of TABLES_IN_DROP_ORDER) {
        if (await this.tableExists(tableName)) await this.dropTable(tableName)
      }
    })
  }

  /** Creates the eight project-stream resource tables. */
  async createDomainTables() {
    await this.createUuidTable("task_types", (table) => {
      table.uuid("project_id", {null: true})
      table.string("name", {null: true})
      table.boolean("default_for_project", {default: false, null: false})
      this.addReplicaTimestamps(table)
    })
    await this.createUuidTable("boards", (table) => {
      table.uuid("project_id", {null: false})
      table.string("name", {default: "Board", null: false})
      this.addReplicaTimestamps(table)
    })
    await this.createUuidTable("board_columns", (table) => {
      table.uuid("board_id", {null: false})
      table.string("name", {null: false})
      table.string("color", {null: true})
      table.integer("position", {null: false})
      this.addReplicaTimestamps(table)
    })
    await this.createUuidTable("tasks", (table) => {
      table.uuid("project_id", {null: true})
      table.uuid("task_type_id", {null: true})
      table.uuid("user_id", {null: true})
      table.uuid("parent_id", {null: true})
      table.string("name", {null: true})
      table.text("description", {null: true})
      table.string("state", {default: "open", null: true})
      table.integer("priority", {null: true})
      this.addReplicaTimestamps(table)
    })
    await this.createUuidTable("task_boards", (table) => {
      table.uuid("board_id", {null: false})
      table.uuid("board_column_id", {null: false})
      table.uuid("task_id", {null: false})
      table.integer("row_number", {null: false})
      this.addReplicaTimestamps(table)
    })
    await this.createUuidTable("task_assigned_users", (table) => {
      table.uuid("task_id", {null: true})
      table.uuid("user_id", {null: true})
      table.uuid("user_assigned_by_id", {null: true})
      this.addReplicaTimestamps(table)
    })
    await this.createUuidTable("comments", (table) => {
      table.uuid("user_id", {null: true})
      table.string("resource_type", {null: true})
      table.uuid("resource_id", {null: true})
      table.bigint("id_per_resource", {null: true})
      table.text("comment", {null: true})
      table.string("automation_kind", {null: true})
      this.addReplicaTimestamps(table)
    })
    await this.createUuidTable("timelogs", (table) => {
      table.uuid("task_id", {null: true})
      table.uuid("user_id", {null: true})
      table.addColumn("date", {isNewColumn: true, null: true, type: "date"})
      table.bigint("time_seconds", {null: true})
      table.bigint("time_transport_seconds", {null: true})
      table.bigint("transport_length", {null: true})
      table.text("comment", {null: true})
      table.boolean("invoiced", {default: false, null: false})
      this.addReplicaTimestamps(table)
    })
  }

  /** Creates private queue/receipt/conflict metadata for this project only. */
  async createBookkeepingTables() {
    await this.createUuidTable("replica_metadata", (table) => {
      table.string("key", {null: false})
      table.text("value", {null: false})
      table.timestamps({null: false})
    })
    await this.createUuidTable("syncs", (table) => {
      table.string("resource_type", {null: false})
      table.uuid("resource_id", {null: false})
      table.string("sync_type", {null: false})
      table.string("command", {null: true})
      table.json("data", {null: false})
      table.json("base_version", {null: true})
      table.uuid("predecessor_mutation_id", {null: true})
      table.bigint("sequence", {null: false})
      table.string("state", {default: "pending", null: false})
      table.string("failure_reason", {null: true})
      table.json("failure_details", {null: true})
      table.timestamps({null: false})
    })
  }

  /** Creates domain and queue lookup indexes. */
  async createIndexes() {
    const indexes = [
      ["boards", ["project_id"]],
      ["board_columns", ["board_id"]],
      ["board_columns", ["board_id", "position"], {unique: true}],
      ["task_types", ["project_id"]],
      ["tasks", ["project_id"]],
      ["tasks", ["task_type_id"]],
      ["tasks", ["user_id"]],
      ["tasks", ["parent_id"]],
      ["task_boards", ["board_id"]],
      ["task_boards", ["board_column_id"]],
      ["task_boards", ["task_id"]],
      ["task_boards", ["board_column_id", "row_number"], {unique: true}],
      ["task_boards", ["task_id", "board_id"], {unique: true}],
      ["task_assigned_users", ["task_id"]],
      ["task_assigned_users", ["user_id"]],
      ["task_assigned_users", ["user_assigned_by_id"]],
      ["task_assigned_users", ["task_id", "user_id"], {unique: true}],
      ["comments", ["resource_type", "resource_id", "created_at"]],
      ["comments", ["resource_type", "resource_id", "id_per_resource"]],
      ["comments", ["user_id"]],
      ["timelogs", ["task_id", "date"]],
      ["timelogs", ["user_id", "date"]],
      ["timelogs", ["date"]],
      ["replica_metadata", ["key"], {unique: true}],
      ["syncs", ["state", "sequence"]],
      ["syncs", ["resource_type", "resource_id", "sequence"]],
      ["syncs", ["predecessor_mutation_id"]]
    ]

    for (const [tableName, columns, options] of indexes) {
      await this.addIndex(
        /** @type {string} */ (tableName),
        /** @type {string[]} */ (columns),
        {...(/** @type {{unique?: boolean} | undefined} */ (options) || {}), ifNotExists: true}
      )
    }
  }

  /**
   * Creates a UUID-primary-key table.
   * @param {string} tableName Destination schema table.
   * @param {(table: import("velocious/build/src/database/table-data/index.js").default) => void} callback Definition callback.
   */
  async createUuidTable(tableName, callback) {
    await this.createTable(tableName, {id: {type: "uuid"}}, callback)
  }

  /**
   * Adds the timestamps shared by replicated resources.
   * @param {import("velocious/build/src/database/table-data/index.js").default} table Table definition.
   */
  addReplicaTimestamps(table) {
    table.timestamps({null: false})
    table.datetime("last_sync_change_at", {null: true})
  }
}

CreateProjectReplica.onDatabases(["projectReplica"])
